import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ProviderShell } from "@/components/provider/ProviderShell";
import { todayET } from "@/lib/dates";

const GROUPS = [["overdue", "Overdue"], ["this_week", "This week"], ["later", "Later"], ["done", "Done"]];
const CHIPS = [["all", "All", null], ["evaluations", "Evaluations", "Evaluation"], ["screenings", "Screenings", "Screening"], ["other", "Other services", "Other"], ["placements", "Placements", "Placement"], ["tasks", "Tasks", "Task"]];
const day = (value) => (value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");

function dueLine(item) {
  if (item.type === "placement") return item.meta;
  if (!item.due_date) return `${item.client_name} · No due date`;
  const today = todayET();
  const due = item.due_date.slice(0, 10);
  if (item.group === "overdue") {
    const late = Math.round((new Date(`${today}T12:00:00`) - new Date(`${due}T12:00:00`)) / 86400000);
    return `${item.client_name} · Due ${day(due)} · ${late} day${late === 1 ? "" : "s"} late`;
  }
  return `${item.client_name} · ${due === today ? "Due today" : `Due ${day(due)}`}`;
}

export default function ProviderMyWorkPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [chip, setChip] = useState("all");
  useEffect(() => {
    api.get("/providers/me/work").then((response) => setData(response.data)).catch((requestError) => setError(formatApiError(requestError)));
  }, []);
  const items = useMemo(() => {
    const kind = CHIPS.find(([key]) => key === chip)?.[2];
    return (data?.items || []).filter((item) => !kind || item.kind === kind);
  }, [data, chip]);
  const counts = data?.counts || {};
  return (
    <ProviderShell title="My work" testId="provider-my-work-page">
      {error && <p className="form-message error-message" data-testid="provider-my-work-error">{error}</p>}
      {data && (
        <>
          <Link className="provider-card provider-link-card" data-testid="provider-progress-notes-cta" to="/provider-portal/progress-notes">
            <div><strong>Submit progress notes</strong><span className="muted">Paid at 30 minutes per approved note — no Connecteam clock-in</span></div>
            <em>Open ›</em>
          </Link>
          <div className="work-chips" data-testid="provider-work-chips">
            {CHIPS.filter(([key]) => key === "all" || counts[key] > 0).map(([key, label]) => (
              <button key={key} className={`work-chip ${chip === key ? "active" : ""}`} data-testid={`provider-work-chip-${key}`} type="button" onClick={() => setChip(key)}>
                {label} · {counts[key] ?? 0}
              </button>
            ))}
          </div>
          {items.length === 0 && <p className="muted" data-testid="provider-my-work-empty">Nothing here right now.</p>}
          {GROUPS.map(([key, label]) => {
            const rows = items.filter((item) => item.group === key);
            if (!rows.length) return null;
            return (
              <section key={key} className="work-group" data-testid={`provider-work-group-${key}`}>
                <h2 className={`work-group-title ${key}`}>{label.toUpperCase()}</h2>
                {rows.map((item) => (
                  <Link key={item.id} className={`work-item ${key === "overdue" ? "late" : ""}`} data-testid={`provider-work-item-${item.id}`} to={`/provider-portal/work/${item.id}`}>
                    <div>
                      <span className={`kind-chip ${item.kind.toLowerCase()}`}>{item.kind}</span>
                      <strong>{item.title}</strong>
                      <small className={key === "overdue" ? "late" : ""}>{dueLine(item)}</small>
                    </div>
                    <em>{item.action} ›</em>
                  </Link>
                ))}
              </section>
            );
          })}
        </>
      )}
    </ProviderShell>
  );
}
